// src/data/blogData.js
// Static fallback blog posts shown when Firestore is empty or unreachable
import blog1Img from '../assets/images/blog1.jpg';
import blog2Img from '../assets/images/blog2.jpg';
import blog3Img from '../assets/images/blog3.jpg';

export const FALLBACK_POSTS = [
  {
    slug: 'reducing-cloud-costs',
    title: 'Reducing Cloud Costs',
    excerpt: 'An overview of practical approaches to cloud cost assessment, resource optimization, and ongoing cost monitoring without affecting existing systems.',
    date: 'March 10, 2025',
    category: 'Cloud Services',
    img: blog1Img,
    content: `
Cloud cost management is one of the most persistent challenges for organizations running modern workloads. Unchecked cloud spending often stems from idle resources, over-provisioned instances, and lack of visibility into consumption patterns.

## Assessment First

Before optimizing, organizations need a clear picture of current spending. A structured cost assessment should map each workload to its associated cost, identify underutilized resources, and flag anomalies in consumption trends.

## Resource Right-Sizing

Over-provisioned compute and storage resources are the most common driver of unnecessary cloud spend. Right-sizing means matching resource allocation to actual workload requirements — not theoretical peak capacity.

Key steps include reviewing CPU and memory utilization averages over 30-90 day periods, identifying instances consistently running below 40% utilization, and evaluating reserved instance and savings plan coverage against actual usage.

## Ongoing Monitoring

Cost optimization is not a one-time activity. Sustainable control requires continuous monitoring, budget alerting, and regular review cycles tied to procurement and architecture decisions.

Teams that integrate cost visibility into their delivery workflows — rather than treating it as a finance function — consistently achieve better outcomes.
    `,
  },
  {
    slug: 'supporting-system-modernization',
    title: 'Supporting System Modernization',
    excerpt: 'This post discusses common challenges in modernizing legacy systems while maintaining security, compliance, and service continuity.',
    date: 'February 22, 2025',
    category: 'Modernization',
    img: blog2Img,
    content: `
Legacy system modernization is one of the most complex undertakings in government and enterprise IT. The challenge is not just technical — it's organizational.

## The Continuity Constraint

Most modernization failures happen because organizations try to move too fast. Cutting over to new systems before parallel operations are validated introduces service disruption.

A more reliable approach runs new and legacy systems in parallel for defined periods, with clear criteria for cutover agreed upon before work begins.

## Security and Compliance

Modernization projects introduce new attack surfaces. Security reviews should be continuous rather than end-of-project, with compliance requirements mapped at the architecture stage.

For government environments, this means FedRAMP alignment, FISMA controls, and agency-specific documentation requirements built into the delivery process from the start.

## Workforce Considerations

Legacy systems often depend on institutional knowledge held by a small number of staff. Modernization planning should include knowledge transfer documentation, training for new system operators, and transition support coverage.
    `,
  },
  {
    slug: 'building-workforce-continuity',
    title: 'Building Workforce Continuity',
    excerpt: 'A look at how workforce planning, oversight, and structured delivery models help maintain stability across long-running IT initiatives.',
    date: 'January 15, 2025',
    category: 'Workforce',
    img: blog3Img,
    content: `
Workforce continuity is often treated as a staffing problem. In practice, it's a delivery problem. Organizations that maintain stable, effective teams over long-running IT programs do so through structure — not just by hiring well.

## The Cost of Turnover

Turnover on long-running programs doesn't just create coverage gaps. It degrades institutional knowledge, introduces delivery inconsistency, and increases onboarding costs.

## Structured Delivery as a Retention Mechanism

Teams that operate within clearly defined processes, with visible progress tracking and accountable management, retain staff at higher rates than those in ambiguous delivery environments.

## Workforce Augmentation Models

For organizations that cannot hire fast enough to keep pace with program demand, workforce augmentation provides a way to scale delivery capacity without compromising quality.

## Planning for Transitions

Every long-running program will eventually experience personnel transitions. Building transition protocols into the program from the start reduces the impact when transitions occur.
    `,
  },
];